import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiLoader, FiSearch } = FiIcons;

const AddressAutocomplete = ({ value, onChange, placeholder, required }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (value !== query) {
      setQuery(value || '');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddresses = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&addressdetails=1&limit=5&countrycodes=it&accept-language=it`,
        {
          signal: abortControllerRef.current.signal,
          headers: {
            'User-Agent': 'TraslochiSmartBergamo/1.0'
          }
        }
      );

      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      
      const formattedSuggestions = data.map(item => ({
        display_name: item.display_name,
        lat: item.lat,
        lon: item.lon,
        address: {
          road: item.address?.road || '',
          house_number: item.address?.house_number || '',
          postcode: item.address?.postcode || '',
          city: item.address?.city || item.address?.town || item.address?.village || '',
          province: item.address?.state || '',
          country: item.address?.country || ''
        }
      }));

      setSuggestions(formattedSuggestions);
      setIsOpen(true);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching addresses:', error);
        setSuggestions([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedSearch = debounce(searchAddresses, 300);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedAddress(null);
    debouncedSearch(value);
    onChange(value);
  };

  const handleSelectAddress = (suggestion) => {
    setSelectedAddress(suggestion);
    setQuery(suggestion.display_name);
    setIsOpen(false);
    onChange(suggestion.display_name);
  };

  const handleInputFocus = () => {
    if (query.length >= 3) {
      searchAddresses(query);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          required={required}
          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          placeholder={placeholder || "Inserisci indirizzo..."}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          ) : (
            <SafeIcon icon={selectedAddress ? FiMapPin : FiSearch} className="text-gray-400 text-lg" />
          )}
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectAddress(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors focus:bg-gray-50 focus:outline-none"
            >
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiMapPin} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 font-medium truncate">
                    {suggestion.address.road && suggestion.address.house_number 
                      ? `${suggestion.address.road} ${suggestion.address.house_number}`
                      : suggestion.display_name.split(',')[0]
                    }
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {suggestion.address.city && suggestion.address.postcode 
                      ? `${suggestion.address.postcode} ${suggestion.address.city}`
                      : suggestion.display_name.split(',').slice(1).join(',').trim()
                    }
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && !isLoading && query.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="px-4 py-3 text-sm text-gray-500 text-center">
            Nessun indirizzo trovato
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;